import { inject, Injectable } from '@angular/core';
import { DurationType, DurationTypes, TripDates, TuitionDates } from '@lx/quote-tool/shared/types';
import { SchoolControllerApiService } from '@lx/shared/api';
import { getLocale } from '@lx/shared/i18n';
import { getCompanyHeader } from '@lx/shared/tokens';
import { CalendarConfig } from '@lx/shared/ui/calendar-field';
import { getDateFromString } from '@lx/shared/utils';
import { Memoize } from '@lx/shared/utils-http';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class TripDatesService {
  private readonly companyHeader = getCompanyHeader();
  private readonly locale = getLocale();
  private readonly schoolControllerApiService = inject(SchoolControllerApiService);

  getTripDates(
    schoolCode: string,
    productCode: string,
    duration: number,
    startDate: string,
    durationType: DurationType = DurationTypes.Weeks,
  ): Observable<TripDates> {
    if (!schoolCode || !productCode || !duration || !startDate) {
      return of(undefined);
    }

    return this.schoolControllerApiService.getTripDates(duration, startDate, schoolCode, productCode, this.locale, this.companyHeader, durationType);
  }

  getTuitionDates(schoolCode: string, productCode: string, duration: number, startDate: string): Observable<TuitionDates> {
    return this.getTripDates(schoolCode, productCode, duration, startDate).pipe(
      map(({ tuitionStartDate, tuitionEndDate }) => ({
        startDate: tuitionStartDate,
        endDate: tuitionEndDate,
      })),
    );
  }

  /**
   * Returns the min, max and disabled dates for the given school code based on the available start dates gotten from the API.
   *
   * We need the disabled dates instead of the start dates, as this is how the calendar component works.
   *
   * This implementation handles the error scenarios and returns fake disabled dates when the API call fails.
   *
   * @param schoolCode the school code for which the disabled dates are to be fetched.
   */
  getCalendarConfig(schoolCode: string): Observable<CalendarConfig> {
    return this.getStartDates(schoolCode).pipe(
      map((startDates) => {
        const minDate = startDates.reduce((min, date) => (date < min ? date : min), startDates[0]);
        const maxDate = startDates.reduce((max, date) => (date > max ? date : max), startDates[0]);
        const disabledDates = this.getAllDatesBetween(minDate, maxDate).filter(
          // we need to compare by strings to get correct results
          (date) => !startDates.find((startDate) => startDate.toDateString() === date.toDateString()),
        );

        return { minDate, maxDate, disabledDates };
      }),
    );
  }

  /**
   * Returns the start dates for the given school code.
   *
   * This implementation handles the error scenarios and returns fake start dates when the API call fails.
   *
   * @param schoolCode the school code for which the start dates are to be fetched.
   */
  @Memoize({ tags: ['getStartDates'], hashFunction: (schoolCode: string) => schoolCode })
  private getStartDates(schoolCode: string): Observable<Date[]> {
    if (!schoolCode) {
      return of(this.getFakeStartDates());
    }

    try {
      return this.schoolControllerApiService
        .findStartDates(schoolCode, this.locale, this.companyHeader)
        .pipe(map((startDates) => startDates.map((date) => getDateFromString(date))));
    } catch (e) {
      return of(this.getFakeStartDates());
    }
  }

  /**
   * Returns fake start dates for the next 3 years without making an API call.
   *
   * This is to be used when API call fails or in any other error scenarios to avoid empty calendars.
   *
   * DO NOT USE THIS METHOD unless it's only for error-related reasons.
   */
  private getFakeStartDates(): Date[] {
    // get the Monday in two weeks time from now
    const seedDate = this.getMondayInTwoWeeks();

    // 52 weeks times 3 years = 156 weeks
    return Array.from({ length: 156 }, (_, i) => {
      // Clone `seedDate` so each iteration gets its own Date instance.
      // Reusing the same object mutates it, making all entries end up as the final date.
      const date = new Date(seedDate);
      date.setDate(date.getDate() + i);
      return date;
    });
  }

  private getMondayInTwoWeeks(): Date {
    const seedDate = new Date();
    seedDate.setDate(seedDate.getDate() + 14);

    while (seedDate.getDay() !== 1) {
      seedDate.setDate(seedDate.getDate() + 1);
    }

    return seedDate;
  }

  private getAllDatesBetween(start: Date, end: Date) {
    const dates = [];
    const currentDate = new Date(start);

    while (currentDate <= end) {
      dates.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return dates;
  }
}
